import {
  DEBTS_COST_FUNDING_PATH, DEBTS_COST_GUARANTEES_PATH, DEBTS_COST_INDEXER_PATH, DEBTS_MARKET_INDEXES_PATH,
  DEBTS_MATURITIES_PATH, DEBTS_MOVEMENTS_PATH,
} from '../shared/debtsNavigation';
import { DEBENTURE_DEBT_TYPE } from '../shared/debtFamily';
import {
  BANK_DEBTS_PATH, NEW_BANK_DEBT_PATH, bankDebtEditPath, bankDebtPath,
} from '../shared/bankDebtRoutes';
import { BANK_DEBT_TYPES } from '../shared/debts.constants';
import { LEGACY_OWNER_PARAM, SCOPE_PARAM } from '../portfolio/scopeParam';
import { levelScopeId, ownerScopeId, type HierarchyLevel } from '../portfolio/portfolioScope';
import { LEGACY_MAPA_TAB_PARAM, MAPA_TABS, type MapaTab } from '../overview-v2/mapa-divida/mapaDaDividaUrl';

export interface DebtsRedirectTarget {
  path: string;
  params?: Readonly<Record<string, string>>;
  translate?: (source: URLSearchParams) => URLSearchParams;
}

export type DebtsRedirectRouteParams = Readonly<Record<string, string | undefined>>;

export type DebtsRedirectResolver = (source: URLSearchParams, routeParams: DebtsRedirectRouteParams) => DebtsRedirectTarget;

export interface LegacyDebtsRedirect {
  routePath: string;
  routeKey: string;
  target: DebtsRedirectTarget | DebtsRedirectResolver;
}

const MOVEMENTS_BY_NATURE: DebtsRedirectTarget = { path: DEBTS_MOVEMENTS_PATH, params: { agruparPor: 'natureza' } };

const REFERENCE_DATE_PARAM = 'mapaData';
const CARTEIRA_PARAM = 'carteira';
const MONTH_PARAM = 'mes';
const DEBT_TYPE_PARAM = 'tipo';
const HIERARCHY_LEVEL_PARAM = 'nivel';
const OBSOLETE_MONTHLY_PARAMS = ['agrupar'] as const;
const INDEXERS_MODE_PARAM = 'modo';
const INDEXERS_CONSULTA_MODE = 'consulta';

const FULL_ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const YEAR_MONTH = /^(\d{4})-(\d{2})$/;
const HIERARCHY_LEVELS: readonly HierarchyLevel[] = ['level1', 'level2', 'level3'];

const endOfMonth = (year: number, month: number): string => (
  new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10)
);

const referenceDateOf = (month: string): string | null => {
  if (FULL_ISO_DATE.test(month)) return month;
  const match = YEAR_MONTH.exec(month);
  return match ? endOfMonth(Number(match[1]), Number(match[2])) : null;
};

const carteiraOf = (debtType: string): string | null => {
  if (debtType === DEBENTURE_DEBT_TYPE) return 'debentures';
  return BANK_DEBT_TYPES.includes(debtType) ? 'bancarias' : null;
};

const scopeOfHierarchyLevel = (value: string): string | null => {
  const separator = value.indexOf(':');
  const level = HIERARCHY_LEVELS[Number(value.slice(0, separator)) - 1];
  const name = value.slice(separator + 1);
  return separator > 0 && level && name ? levelScopeId(level, name) : null;
};

export const legacyMonthlyMovementsSearch = (source: URLSearchParams): URLSearchParams => {
  const params = new URLSearchParams(source);
  const month = params.get(MONTH_PARAM);
  const debtType = params.get(DEBT_TYPE_PARAM);
  const owner = params.get(LEGACY_OWNER_PARAM);
  const level = params.get(HIERARCHY_LEVEL_PARAM);
  [MONTH_PARAM, DEBT_TYPE_PARAM, LEGACY_OWNER_PARAM, HIERARCHY_LEVEL_PARAM, ...OBSOLETE_MONTHLY_PARAMS]
    .forEach((name) => params.delete(name));
  const referenceDate = month ? referenceDateOf(month) : null;
  if (referenceDate) params.set(REFERENCE_DATE_PARAM, referenceDate);
  const carteira = debtType ? carteiraOf(debtType) : null;
  if (carteira) params.set(CARTEIRA_PARAM, carteira);
  const scope = owner ? ownerScopeId(owner) : level && scopeOfHierarchyLevel(level);
  if (scope && !params.has(SCOPE_PARAM)) params.set(SCOPE_PARAM, scope);
  return params;
};

export const legacyIndexersTarget = (source: URLSearchParams): DebtsRedirectTarget => {
  if (source.get(INDEXERS_MODE_PARAM) !== INDEXERS_CONSULTA_MODE) return { path: DEBTS_COST_INDEXER_PATH };
  return {
    path: DEBTS_MARKET_INDEXES_PATH,
    translate: (params) => {
      const next = new URLSearchParams(params);
      next.delete(INDEXERS_MODE_PARAM);
      return next;
    },
  };
};

const LEGACY_MAPA_TAB_TARGETS: Readonly<Record<MapaTab, DebtsRedirectTarget>> = {
  movimentacao: { path: DEBTS_MOVEMENTS_PATH },
  naturezaModalidade: MOVEMENTS_BY_NATURE,
  custoCaptacao: { path: DEBTS_COST_FUNDING_PATH },
  custoIndexador: { path: DEBTS_COST_INDEXER_PATH },
  tirDuration: { path: DEBTS_MATURITIES_PATH },
};

const isMapaTab = (value: string | null): value is MapaTab =>
  !!value && (MAPA_TABS as readonly string[]).includes(value);

export const legacyMapaTarget = (params: URLSearchParams): DebtsRedirectTarget => {
  const tab = params.get(LEGACY_MAPA_TAB_PARAM);
  return LEGACY_MAPA_TAB_TARGETS[isMapaTab(tab) ? tab : MAPA_TABS[0]];
};

const LEGACY_EDIT_ID_PARAM = 'editId';

const withoutEditId = (params: URLSearchParams): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete(LEGACY_EDIT_ID_PARAM);
  return next;
};

export const legacyNewDebtTarget = (source: URLSearchParams): DebtsRedirectTarget => {
  const editId = source.get(LEGACY_EDIT_ID_PARAM);
  return { path: editId ? bankDebtEditPath(editId) : NEW_BANK_DEBT_PATH, translate: withoutEditId };
};

export const legacyBankDebtScheduleTarget = (
  _source: URLSearchParams,
  routeParams: DebtsRedirectRouteParams,
): DebtsRedirectTarget => ({ path: routeParams.id ? bankDebtPath(routeParams.id) : BANK_DEBTS_PATH });

export const LEGACY_DEBTS_REDIRECTS: readonly LegacyDebtsRedirect[] = [
  { routePath: 'new-debt', routeKey: 'debts-new-debt', target: legacyNewDebtTarget },
  { routePath: 'my-debts/:id/schedule', routeKey: 'debts-my-debts-schedule', target: legacyBankDebtScheduleTarget },
  { routePath: 'indexers', routeKey: 'debts-indexers', target: legacyIndexersTarget },
  { routePath: 'map', routeKey: 'debts-map', target: legacyMapaTarget },
  { routePath: 'map/movements', routeKey: 'debts-map-movements', target: { path: DEBTS_MOVEMENTS_PATH } },
  { routePath: 'map/nature-modality', routeKey: 'debts-map-nature-modality', target: MOVEMENTS_BY_NATURE },
  { routePath: 'map/funding-cost', routeKey: 'debts-map-funding-cost', target: { path: DEBTS_COST_FUNDING_PATH } },
  { routePath: 'map/indexer-cost', routeKey: 'debts-map-indexer-cost', target: { path: DEBTS_COST_INDEXER_PATH } },
  { routePath: 'map/irr-duration', routeKey: 'debts-map-irr-duration', target: { path: DEBTS_MATURITIES_PATH } },
  { routePath: 'guarantee-costs', routeKey: 'debts-guarantee-costs', target: { path: DEBTS_COST_GUARANTEES_PATH } },
  {
    routePath: 'reports/monthly-movements',
    routeKey: 'debts-reports-monthly-movements',
    target: { path: DEBTS_MOVEMENTS_PATH, translate: legacyMonthlyMovementsSearch },
  },
];

export const resolveRedirectTarget = (
  target: DebtsRedirectTarget | DebtsRedirectResolver,
  source: URLSearchParams,
  routeParams: DebtsRedirectRouteParams = {},
): DebtsRedirectTarget => (typeof target === 'function' ? target(source, routeParams) : target);

export const redirectSearch = (target: DebtsRedirectTarget, source: URLSearchParams): URLSearchParams => {
  const params = new URLSearchParams(target.translate ? target.translate(source) : source);
  params.delete(LEGACY_MAPA_TAB_PARAM);
  Object.entries(target.params ?? {}).forEach(([key, value]) => params.set(key, value));
  return params;
};
